/**
 * In-memory cache for decrypted data with LRU eviction
 * Prevents re-decryption of the same data during navigation
 */

import { logger } from '@/utils/logger';

interface CacheEntry<T> {
  data: T;
  timestamp: number;
}

const DEFAULT_TTL = 5 * 60 * 1000; // 5 minutes
const MAX_CACHE_SIZE = 1000;

class DecryptionCache {
  private cache = new Map<string, CacheEntry<unknown>>();
  private accessOrder: string[] = [];
  private ttl: number;
  private maxSize: number;

  constructor(ttl = DEFAULT_TTL, maxSize = MAX_CACHE_SIZE) {
    this.ttl = ttl;
    this.maxSize = maxSize;
  }

  /**
   * Generate a cache key from encrypted data
   */
  private generateKey(encrypted: string, nonce: string): string {
    // Use first 32 chars of encrypted + nonce for efficiency
    return `${encrypted.slice(0, 32)}_${nonce}`;
  }

  /**
   * Get cached decrypted value
   */
  get<T>(encrypted: string, nonce: string): T | null {
    const key = this.generateKey(encrypted, nonce);
    const entry = this.cache.get(key);

    if (!entry) {
      return null;
    }

    // Check TTL
    if (Date.now() - entry.timestamp > this.ttl) {
      this.cache.delete(key);
      this.removeFromAccessOrder(key);
      return null;
    }

    // Update access order for LRU
    this.updateAccessOrder(key);
    
    return entry.data as T;
  }

  /**
   * Set decrypted value in cache
   */
  set<T>(encrypted: string, nonce: string, data: T): void {
    const key = this.generateKey(encrypted, nonce);

    // Evict if at capacity
    while (this.cache.size >= this.maxSize) {
      this.evictLRU();
    }

    this.cache.set(key, {
      data,
      timestamp: Date.now()
    });

    this.updateAccessOrder(key);
  }

  /**
   * Check if value exists and is valid
   */
  has(encrypted: string, nonce: string): boolean {
    const key = this.generateKey(encrypted, nonce);
    const entry = this.cache.get(key);

    if (!entry) return false;
    
    if (Date.now() - entry.timestamp > this.ttl) {
      this.cache.delete(key);
      this.removeFromAccessOrder(key);
      return false;
    }

    return true;
  }

  /**
   * Get multiple values at once
   */
  getMany<T>(items: Array<{ encrypted: string; nonce: string }>): Array<{ index: number; cached: T | null }> {
    return items.map((item, index) => ({
      index,
      cached: this.get<T>(item.encrypted, item.nonce)
    }));
  }

  /**
   * Set multiple values at once
   */
  setMany<T>(items: Array<{ encrypted: string; nonce: string; data: T }>): void {
    for (const item of items) {
      this.set(item.encrypted, item.nonce, item.data);
    }
  }

  /**
   * Clear all cached data
   */
  clear(): void {
    this.cache.clear();
    this.accessOrder = [];
    logger.debug('[DecryptionCache] Cache cleared');
  }

  /**
   * Clear expired entries
   */
  clearExpired(): number {
    const now = Date.now();
    let cleared = 0;

    for (const [key, entry] of this.cache.entries()) {
      if (now - entry.timestamp > this.ttl) {
        this.cache.delete(key);
        this.removeFromAccessOrder(key);
        cleared++;
      }
    }

    if (cleared > 0) {
      logger.debug(`[DecryptionCache] Cleared ${cleared} expired entries`);
    }

    return cleared;
  }

  /**
   * Get cache statistics
   */
  getStats(): { size: number; maxSize: number; ttl: number } {
    return {
      size: this.cache.size,
      maxSize: this.maxSize,
      ttl: this.ttl
    };
  }

  private updateAccessOrder(key: string): void {
    this.removeFromAccessOrder(key);
    this.accessOrder.push(key);
  }

  private removeFromAccessOrder(key: string): void {
    const index = this.accessOrder.indexOf(key);
    if (index > -1) {
      this.accessOrder.splice(index, 1);
    }
  }

  private evictLRU(): void {
    const oldestKey = this.accessOrder.shift();
    if (oldestKey) {
      this.cache.delete(oldestKey);
    }
  }
}

// Singleton instance
export const decryptionCache = new DecryptionCache();

// Convenience functions
export const getCachedDecrypted = <T>(encrypted: string, nonce: string): T | null => {
  return decryptionCache.get<T>(encrypted, nonce);
};

export const setCachedDecrypted = <T>(encrypted: string, nonce: string, data: T): void => {
  decryptionCache.set(encrypted, nonce, data);
};

export const hasCachedDecrypted = (encrypted: string, nonce: string): boolean => {
  return decryptionCache.has(encrypted, nonce);
};

export const clearDecryptionCache = (): void => {
  decryptionCache.clear();
};

// Periodic cleanup (run every 2 minutes)
if (typeof window !== 'undefined') {
  setInterval(() => {
    decryptionCache.clearExpired();
  }, 2 * 60 * 1000);
}
